/**
 * PATH: packages/job-hub/src/jobs/qa/EditableCell.tsx
 * FILE_ID: JOBHUB-QA-CELL-001
 * PURPOSE: A single auto-growing "clay" cell for the QA build table. Grows to fit
 *          content (textarea scrollHeight), commits on blur, reverts on Escape.
 * WHY:     Operator: "cells like clay … it doesn't auto expand". Extracted from
 *          QaChecklistTable so the table orchestrator stays under the line limit.
 * RELATED:
 * - ./QaChecklistTable.tsx (renders one per editable column)
 * - ./checklistTable.ts (commitCell decides what an edit commits)
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { commitCell } from './checklistTable'

export function EditableCell({
  value,
  singleLine = false,
  allowEmpty = true,
  disabled = false,
  placeholder,
  ariaLabel,
  onCommit,
}: {
  value: string
  singleLine?: boolean
  allowEmpty?: boolean
  disabled?: boolean
  placeholder?: string
  ariaLabel: string
  onCommit: (next: string) => void
}) {
  const [draft, setDraft] = useState(value ?? '')
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => setDraft(value ?? ''), [value])

  // Auto-size to content so cells expand as the operator types. Runs in a layout
  // effect so there's no flicker.
  const resize = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])
  useLayoutEffect(() => {
    resize()
  }, [draft, resize])

  const commit = () => {
    const next = commitCell(value ?? '', draft, { allowEmpty })
    if (next === null) {
      setDraft(value ?? '')
      return
    }
    onCommit(next)
  }

  return (
    <textarea
      ref={ref}
      rows={1}
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDraft(value ?? '')
          e.currentTarget.blur()
          return
        }
        // Single-line cells (title/product) commit on Enter; multiline cells keep
        // Enter for newlines and commit with Cmd/Ctrl+Enter (or blur).
        if (e.key === 'Enter' && (singleLine || e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
      className={cn(
        'block w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm text-foreground',
        'hover:border-border focus:border-primary focus:bg-background focus:outline-none focus:ring-1 focus:ring-primary',
        'placeholder:text-muted-foreground/60 disabled:opacity-60 leading-snug',
        singleLine && 'font-medium',
      )}
    />
  )
}

export default EditableCell
